/* ──────────────────────────────────────────────────────────────
   Document detection / perspective pipeline — ported verbatim from
   pwa-test/index.html. Pure CV logic, no React. Relies on the global
   `cv` (OpenCV.js) being ready — see ensureOpenCv().

   The detection math is kept identical to the tested harness; only the
   global `State` side-effects were replaced with returned values.
   ────────────────────────────────────────────────────────────── */

/* OpenCV.js exposes a global `cv` once the asm.js runtime is initialized. */
declare const cv: any

export interface Pt {
  x: number
  y: number
}

export interface DetectDebug {
  maskCanvas: HTMLCanvasElement
  candidates: Array<{ score: number; corners: Pt[] }>
  confidence: number
  procW: number
  procH: number
}

export interface DetectResult {
  corners: Pt[]
  conf: number
  debug: DetectDebug | null
}

/* Auto-accept confidence threshold — detections above this skip the
   manual corner screen. Calibrated against the test images. */
export const AUTO_CONF = 0.8

/* ── OpenCV loader (singleton) ───────────────────────────────── */
let cvPromise: Promise<void> | null = null

export function ensureOpenCv(): Promise<void> {
  if (cvPromise) return cvPromise
  cvPromise = new Promise<void>((resolve, reject) => {
    const w = window as unknown as { cv?: any }
    const ready = () => {
      const g = (window as unknown as { cv?: any }).cv
      if (g && g.Mat) resolve()
      else if (g) g.onRuntimeInitialized = () => resolve()
      else reject(new Error('OpenCV tanımlanamadı'))
    }
    if (w.cv && w.cv.Mat) {
      resolve()
      return
    }
    const existing = document.getElementById('opencv-js') as HTMLScriptElement | null
    if (existing) {
      if (w.cv && w.cv.Mat) resolve()
      else if (w.cv) w.cv.onRuntimeInitialized = () => resolve()
      else existing.addEventListener('load', ready)
      return
    }
    const s = document.createElement('script')
    s.id = 'opencv-js'
    s.async = true
    s.src = '/opencv-3.4.20.js?v=3.4.20'
    s.onload = ready
    s.onerror = () => reject(new Error('OpenCV indirilemedi'))
    document.body.appendChild(s)
  })
  return cvPromise
}

/* ── Detection ───────────────────────────────────────────────── */

export function detectDocument(srcMat: any): DetectResult | null {
  // A reduced/custom OpenCV build may lack an API — never crash the page,
  // degrade to manual mode instead.
  try {
    return detectDocumentImpl(srcMat)
  } catch (err) {
    console.error('detectDocument hata:', err)
    return null
  }
}

function detectDocumentImpl(srcMat: any): DetectResult | null {
  const PROC_W = 1000
  const procH = Math.round((srcMat.rows * PROC_W) / srcMat.cols)

  const small = new cv.Mat()
  cv.resize(srcMat, small, new cv.Size(PROC_W, procH), 0, 0, cv.INTER_AREA)

  // ── Primary: saturation + brightness mask ──
  const { sat, val } = buildSatVal(small, PROC_W, procH)

  const satBlur = new cv.Mat()
  const valBlur = new cv.Mat()
  cv.GaussianBlur(sat, satBlur, new cv.Size(5, 5), 0)
  cv.GaussianBlur(val, valBlur, new cv.Size(5, 5), 0)

  const satMask = new cv.Mat()
  const valMask = new cv.Mat()
  cv.threshold(satBlur, satMask, 0, 255, cv.THRESH_BINARY_INV + cv.THRESH_OTSU)
  cv.threshold(valBlur, valMask, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU)

  const mask = cv.Mat.zeros(procH, PROC_W, cv.CV_8UC1)
  const pd = mask.data
  const smd = satMask.data
  const vmd = valMask.data
  for (let j = 0; j < pd.length; j++) pd[j] = smd[j] && vmd[j] ? 255 : 0

  const k = cv.Mat.ones(5, 5, cv.CV_8U)
  cv.erode(mask, mask, k)
  cv.dilate(mask, mask, k)
  k.delete()
  satMask.delete()
  valMask.delete()
  valBlur.delete()

  // ── Secondary (fallback): grayscale edge maps ──
  const gray = new cv.Mat()
  cv.cvtColor(small, gray, cv.COLOR_RGBA2GRAY)
  const blurred = new cv.Mat()
  cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0)

  const candidates: Array<{ corners: Pt[]; score: number; conf: number }> = []

  collectQuads(mask, PROC_W, procH, cv.RETR_EXTERNAL, candidates)

  const edgeSources = [
    () => makeEdgesCanny(blurred, 30, 90),
    () => makeEdgesCanny(blurred, 60, 180),
    () => makeEdgesOtsu(blurred),
    () => makeEdgesAdaptive(blurred),
  ]
  for (const make of edgeSources) {
    const edges = make()
    collectQuads(edges, PROC_W, procH, cv.RETR_EXTERNAL, candidates)
    edges.delete()
  }

  let best: { corners: Pt[]; score: number; conf: number } | null = null
  for (const c of candidates) {
    if (!best || c.score > best.score) best = c
  }

  const sx = srcMat.cols / PROC_W
  const sy = srcMat.rows / procH
  const debug: DetectDebug = {
    maskCanvas: maskToCanvas(mask, PROC_W, procH),
    candidates: candidates.map((c) => ({
      score: c.score,
      corners: c.corners.map((p) => ({ x: p.x * sx, y: p.y * sy })),
    })),
    confidence: best ? best.conf : 0,
    procW: PROC_W,
    procH,
  }

  small.delete()
  sat.delete()
  val.delete()
  satBlur.delete()
  mask.delete()
  gray.delete()
  blurred.delete()

  if (!best) return null

  return {
    corners: best.corners.map((p) => ({ x: p.x * sx, y: p.y * sy })),
    conf: best.conf,
    debug,
  }
}

// RGBA Mat -> saturation (S) + brightness (V) channels, single pass (build-agnostic).
function buildSatVal(rgba: any, w: number, h: number): { sat: any; val: any } {
  const src = rgba.data
  const sat = cv.Mat.zeros(h, w, cv.CV_8UC1)
  const val = cv.Mat.zeros(h, w, cv.CV_8UC1)
  const sd = sat.data
  const vd = val.data
  for (let i = 0, j = 0; j < sd.length; i += 4, j++) {
    const r = src[i]
    const g = src[i + 1]
    const b = src[i + 2]
    const max = r > g ? (r > b ? r : b) : g > b ? g : b
    const min = r < g ? (r < b ? r : b) : g < b ? g : b
    vd[j] = max
    sd[j] = max === 0 ? 0 : ((max - min) * 255) / max | 0
  }
  return { sat, val }
}

function maskToCanvas(mask: any, w: number, h: number): HTMLCanvasElement {
  const cvs = document.createElement('canvas')
  cvs.width = w
  cvs.height = h
  const ctx = cvs.getContext('2d')!
  const img = ctx.createImageData(w, h)
  const m = mask.data
  for (let j = 0, p = 0; j < m.length; j++, p += 4) {
    const on = m[j] > 0
    img.data[p] = 0
    img.data[p + 1] = on ? 255 : 0
    img.data[p + 2] = 0
    img.data[p + 3] = on ? 255 : 0
  }
  ctx.putImageData(img, 0, 0)
  return cvs
}

function makeEdgesCanny(blurred: any, low: number, high: number): any {
  const edges = new cv.Mat()
  cv.Canny(blurred, edges, low, high)
  const kernel = cv.Mat.ones(5, 5, cv.CV_8U)
  cv.dilate(edges, edges, kernel)
  kernel.delete()
  return edges
}

function makeEdgesOtsu(blurred: any): any {
  const bin = new cv.Mat()
  cv.threshold(blurred, bin, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU)
  const edges = new cv.Mat()
  cv.Canny(bin, edges, 50, 150)
  const kernel = cv.Mat.ones(5, 5, cv.CV_8U)
  cv.dilate(edges, edges, kernel)
  bin.delete()
  kernel.delete()
  return edges
}

function makeEdgesAdaptive(blurred: any): any {
  const bin = new cv.Mat()
  cv.adaptiveThreshold(blurred, bin, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 25, 10)
  const edges = new cv.Mat()
  cv.Canny(bin, edges, 50, 150)
  const kernel = cv.Mat.ones(5, 5, cv.CV_8U)
  cv.dilate(edges, edges, kernel)
  bin.delete()
  kernel.delete()
  return edges
}

function collectQuads(
  binMat: any,
  w: number,
  h: number,
  mode: any,
  out: Array<{ corners: Pt[]; score: number; conf: number }>,
): void {
  const totalArea = w * h
  const contours = new cv.MatVector()
  const hier = new cv.Mat()
  cv.findContours(binMat, contours, hier, mode, cv.CHAIN_APPROX_SIMPLE)

  for (let i = 0; i < contours.size(); i++) {
    const c = contours.get(i)
    const area = cv.contourArea(c)
    if (area < totalArea * 0.15) {
      c.delete()
      continue
    }
    const corners = quadFromContour(c)
    c.delete()
    if (!corners) continue
    const ordered = orderCorners(corners)
    const res = scoreQuad(ordered, totalArea, w, h)
    if (res === null) continue
    out.push({ corners: ordered, score: res.score, conf: res.conf })
  }

  contours.delete()
  hier.delete()
}

function quadFromContour(c: any): Pt[] | null {
  const peri = cv.arcLength(c, true)
  for (let f = 0.02; f <= 0.08; f += 0.01) {
    const a = new cv.Mat()
    cv.approxPolyDP(c, a, f * peri, true)
    if (a.rows === 4 && cv.isContourConvex(a)) {
      const pts: Pt[] = []
      for (let k = 0; k < 4; k++) {
        pts.push({ x: a.data32S[k * 2], y: a.data32S[k * 2 + 1] })
      }
      a.delete()
      return pts
    }
    a.delete()
  }
  return extremeCorners(c)
}

function extremeCorners(c: any): Pt[] | null {
  const d = c.data32S
  if (!d || d.length < 8) return null
  let tl: Pt | undefined
  let tr: Pt | undefined
  let bl: Pt | undefined
  let br: Pt | undefined
  let sMin = Infinity
  let sMax = -Infinity
  let dMin = Infinity
  let dMax = -Infinity
  for (let i = 0; i < d.length; i += 2) {
    const x = d[i]
    const y = d[i + 1]
    const sum = x + y
    const dif = x - y
    if (sum < sMin) {
      sMin = sum
      tl = { x, y }
    }
    if (sum > sMax) {
      sMax = sum
      br = { x, y }
    }
    if (dif < dMin) {
      dMin = dif
      bl = { x, y }
    }
    if (dif > dMax) {
      dMax = dif
      tr = { x, y }
    }
  }
  if (!tl || !tr || !bl || !br) return null
  return [tl, tr, br, bl]
}

function scoreQuad(
  pts: Pt[],
  totalArea: number,
  w: number,
  h: number,
): { score: number; conf: number } | null {
  const [tl, tr, br, bl] = pts
  const top = Math.hypot(tr.x - tl.x, tr.y - tl.y)
  const right = Math.hypot(br.x - tr.x, br.y - tr.y)
  const bottom = Math.hypot(br.x - bl.x, br.y - bl.y)
  const left = Math.hypot(bl.x - tl.x, bl.y - tl.y)
  if (Math.min(top, right, bottom, left) < 50) return null

  const areaRatio = polygonArea(pts) / totalArea
  if (areaRatio < 0.06 || areaRatio > 0.9) return null

  const ratio = (top + bottom) / 2 / ((left + right) / 2)
  if (ratio < 0.3 || ratio > 3.3) return null

  const mx = w * 0.02
  const my = h * 0.02
  const tooClose = pts.some((p) => p.x < mx || p.x > w - mx || p.y < my || p.y > h - my)
  if (tooClose) return null

  const angles = [
    angleAt(bl, tl, tr),
    angleAt(tl, tr, br),
    angleAt(tr, br, bl),
    angleAt(br, bl, tl),
  ]
  const angleErr = angles.reduce((s, a) => s + Math.abs(a - 90), 0) / 4
  if (angleErr > 35) return null

  const sizeScore = areaRatio
  const angleScore = Math.max(0, 1 - angleErr / 35)
  const a4Ratio = Math.min(Math.abs(ratio - 1 / Math.SQRT2), Math.abs(ratio - Math.SQRT2))
  const ratioScore = Math.max(0, 1 - a4Ratio / 1.5)
  const score = sizeScore * 2.5 + angleScore * 2.0 + ratioScore * 1.5

  const areaConf = Math.max(0, Math.min(1, (areaRatio - 0.15) / 0.45))
  const conf = angleScore * 0.6 + ratioScore * 0.25 + areaConf * 0.15
  return { score, conf }
}

function polygonArea(pts: Pt[]): number {
  let s = 0
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    s += a.x * b.y - b.x * a.y
  }
  return Math.abs(s) / 2
}

function angleAt(a: Pt, b: Pt, c: Pt): number {
  const v1x = a.x - b.x
  const v1y = a.y - b.y
  const v2x = c.x - b.x
  const v2y = c.y - b.y
  const dot = v1x * v2x + v1y * v2y
  const len = Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y) || 1
  return (Math.acos(Math.max(-1, Math.min(1, dot / len))) * 180) / Math.PI
}

function orderCorners(pts: Pt[]): Pt[] {
  const bySum = [...pts].sort((a, b) => a.x + a.y - (b.x + b.y))
  const byDiff = [...pts].sort((a, b) => a.x - a.y - (b.x - b.y))
  return [bySum[0], byDiff[3], bySum[3], byDiff[0]]
}

export function defaultCorners(w: number, h: number): Pt[] {
  const A4 = Math.SQRT2
  let rectW = w * 0.7
  let rectH = rectW * A4
  if (rectH > h * 0.85) {
    rectH = h * 0.85
    rectW = rectH / A4
  }
  const mx = (w - rectW) / 2
  const my = (h - rectH) / 2
  return [
    { x: mx, y: my },
    { x: mx + rectW, y: my },
    { x: mx + rectW, y: my + rectH },
    { x: mx, y: my + rectH },
  ]
}

/* ── Perspective warp ────────────────────────────────────────── */
export async function warpToBlob(
  photoMat: any,
  corners: Pt[],
  resultCanvas: HTMLCanvasElement,
): Promise<{ blob: Blob; width: number; height: number }> {
  const [tl, tr, br, bl] = corners
  const outW = Math.round(
    Math.max(Math.hypot(tr.x - tl.x, tr.y - tl.y), Math.hypot(br.x - bl.x, br.y - bl.y)),
  )
  const outH = Math.round(
    Math.max(Math.hypot(bl.x - tl.x, bl.y - tl.y), Math.hypot(br.x - tr.x, br.y - tr.y)),
  )

  const srcPts = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y])
  const dstPts = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, outW - 1, 0, outW - 1, outH - 1, 0, outH - 1])

  const M = cv.getPerspectiveTransform(srcPts, dstPts)
  const dst = new cv.Mat()
  cv.warpPerspective(
    photoMat,
    dst,
    M,
    new cv.Size(outW, outH),
    cv.INTER_LINEAR,
    cv.BORDER_CONSTANT,
    new cv.Scalar(),
  )
  cv.imshow(resultCanvas, dst)

  const blob: Blob = await new Promise((res) =>
    resultCanvas.toBlob((b) => res(b as Blob), 'image/jpeg', 0.92),
  )
  const width = dst.cols
  const height = dst.rows

  srcPts.delete()
  dstPts.delete()
  M.delete()
  dst.delete()

  return { blob, width, height }
}

/* Read a File into a cv.Mat (full resolution) via an offscreen canvas. */
export async function fileToMat(file: File): Promise<any> {
  const bmp = await createImageBitmap(file)
  const cvs = document.createElement('canvas')
  cvs.width = bmp.width
  cvs.height = bmp.height
  cvs.getContext('2d')!.drawImage(bmp, 0, 0)
  if (bmp.close) bmp.close()
  return cv.imread(cvs)
}
